import { Model, Factory } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  rand,
  randCompanyName,
  randEmail,
  randPhoneNumber,
  randNumber,
  randBoolean,
  randFullName,
  randUrl,
} from '@ngneat/falso'

import type { SchemaAttributes } from '../types.js'
import type { MirageServer, MockServer } from './server.js'
import {
  sortable,
  pageable,
  filterable,
  PaginationError,
  createErrorResponse,
} from './utils.js'

type WholesalerModel = SchemaAttributes<'Wholesaler'>

export const model = Model as ModelDefinition<WholesalerModel>

export const factory = Factory.extend<WholesalerModel>({
  name() {
    return randCompanyName()
  },
  email() {
    return randEmail()
  },
  phone_number() {
    return randPhoneNumber()
  },
  register_number() {
    return randNumber({ min: 100000, max: 999999 }).toString()
  },
  wholesaler_type() {
    return rand(['EMAIL', 'ORDER_GW'])
  },
  archived() {
    return randBoolean()
  },
  api_wholesaler() {
    return null
  },
  object_id() {
    return randNumber({ min: 1, max: 999999 }).toString()
  },
  customer_number() {
    return randNumber({ min: 100000, max: 999999 }).toString()
  },
  has_same_credentials_with_unique_customer_number() {
    return randBoolean()
  },
})

export function routes(server: MockServer) {
  server.get(
    '/stock/wholesalers',
    function (this: MirageServer, schema, request) {
      const wholesalersList = schema.all('wholesaler')
      const filtered = filterable(wholesalersList, request, {
        search: (item, value) =>
          (item.attrs.name as string)
            ?.toLowerCase()
            .includes(value?.toLowerCase()) ||
          (item.attrs.email as string)
            ?.toLowerCase()
            .includes(value?.toLowerCase()) ||
          (item.attrs.phone_number as string)
            ?.toLowerCase()
            .includes(value?.toLowerCase()),
        archived: (item, value) =>
          value === 'true' ? item.attrs.archived : !item.attrs.archived,
      })

      // 2) Then apply sorting
      const sorted = sortable(filtered, request)

      // 3) Finally apply pagination
      try {
        const { data, meta } = pageable(sorted, request)

        return {
          ...this.serialize(data),
          meta: {
            pagination: meta,
            choices: {
              wholesaler_type: [
                { label: 'Email', value: 'EMAIL' },
                { label: 'Integration', value: 'ORDER_GW' },
              ],
            },
          },
        }
      } catch (e) {
        if (e instanceof PaginationError) {
          return createErrorResponse({
            status: 404,
            errors: [{ detail: e.message }],
          })
        }

        // Fallback for unexpected errors
        return createErrorResponse({
          status: 500,
          errors: [{ detail: 'Internal Server Error' }],
        })
      }
    },
  )

  server.get(
    '/stock/wholesalers/:id',
    function (this: MirageServer, schema, request) {
      const id = request.params.id
      const wholesaler = schema.find('wholesaler', id)
      if (!wholesaler) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `Wholesaler with id ${id} not found` }],
        })
      }

      return wholesaler
    },
  )

  server.post(
    '/stock/wholesalers',
    function (this: MirageServer, schema, request) {
      const attrs = JSON.parse(request.requestBody)

      // If no attributes, fail with 400 error
      if (!attrs?.data?.attributes) {
        return createErrorResponse({
          status: 400,
          errors: [{ detail: 'Missing "attributes" in request body' }],
        })
      }

      // Check for duplicate name (among all wholesalers, active and archived)
      const wholesalerData = attrs.data.attributes
      const existingWholesaler = schema.findBy('wholesaler', (wholesaler) => {
        return wholesaler.name === wholesalerData.name
      })

      if (existingWholesaler) {
        return createErrorResponse({
          status: 400,
          errors: [
            {
              code: 'unique',
              detail: 'A wholesaler already exists with this name',
              pointer: '/data/attributes/name',
            },
          ],
        })
      }

      const wholesaler = schema.create('wholesaler', wholesalerData)
      return wholesaler
    },
  )

  server.patch('/stock/wholesalers/:id', async function (schema, request) {
    const id = request.params.id
    const attrs = JSON.parse(request.requestBody)
    const wholesaler = schema.find('wholesaler', id)
    if (!wholesaler) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Wholesaler with id ${id} not found` }],
      })
    }

    const updatedWholesaler = wholesaler.update(attrs.data.attributes)
    return updatedWholesaler
  })

  server.delete(
    '/stock/wholesalers/:id',
    function (this: MirageServer, schema, request) {
      const id = request.params.id
      const wholesaler = schema.find('wholesaler', id)
      if (!wholesaler) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `Wholesaler with id ${id} not found` }],
        })
      }

      wholesaler.update({ archived: true })
      return wholesaler
    },
  )

  server.post(
    '/stock/wholesalers/:id/restore',
    function (this: MirageServer, schema, request) {
      const id = request.params.id
      const wholesaler = schema.find('wholesaler', id)
      if (!wholesaler) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `Wholesaler with id ${id} not found` }],
        })
      }

      wholesaler.update({ archived: false })
      return wholesaler
    },
  )

  // Order gateway wholesalers endpoint
  server.get('/stock/order-gateway/wholesalers', function () {
    return {
      data: [
        {
          id: '12',
          type: 'OrderGwWholesaler',
          attributes: {
            supplier_code: 'covetrus',
            supplier_name: 'Covetrus',
            supplier_type: 'Wholesaler',
            business_id: null,
            contact_person: randFullName(),
            contact_phone: randPhoneNumber(),
            contact_email: randEmail(),
            email: randEmail(),
            url: randUrl(),
            user_specific_login: true,
            has_same_credentials_with_unique_customer_number: false,
            countries: [
              {
                country_code: 'GB',
                country_name:
                  'United Kingdom of Great Britain and Northern Ireland',
              },
            ],
          },
        },
        {
          id: '13',
          type: 'OrderGwWholesaler',
          attributes: {
            supplier_code: 'mwi',
            supplier_name: 'Centaur',
            supplier_type: 'Wholesaler',
            business_id: null,
            contact_person: randFullName(),
            contact_phone: randPhoneNumber(),
            contact_email: randEmail(),
            email: randEmail(),
            url: randUrl(),
            user_specific_login: true,
            has_same_credentials_with_unique_customer_number: false,
            countries: [
              {
                country_code: 'GB',
                country_name:
                  'United Kingdom of Great Britain and Northern Ireland',
              },
            ],
          },
        },
      ],
      links: {
        first: null,
        last: null,
        next: null,
        prev: null,
      },
      meta: {
        pagination: {
          page: 1,
          pages: 1,
          count: 2,
        },
      },
    }
  })
}

export function seeds(server: MockServer) {
  // Active wholesalers
  server.create('wholesaler', {
    archived: false,
    wholesaler_type: 'EMAIL',
  })
  server.create('wholesaler', {
    archived: false,
    wholesaler_type: 'ORDER_GW',
  })
  server.create('wholesaler', {
    archived: false,
    wholesaler_type: 'EMAIL',
  })
  server.create('wholesaler', {
    archived: false,
    wholesaler_type: 'EMAIL',
  })

  // Archived wholesalers for filtering tests
  server.create('wholesaler', {
    archived: true,
    wholesaler_type: 'EMAIL',
  })
  server.create('wholesaler', {
    archived: true,
    wholesaler_type: 'EMAIL',
  })

  // EMAIL wholesaler with all legacy fields for testing
  server.create('wholesaler', {
    name: 'Legacy Wholesaler Inc',
    archived: false,
    wholesaler_type: 'EMAIL',
    email: 'legacy@example.com',
    phone_number: '+1 555 123 4567',
    customer_number: '999999',
    register_number: '777777',
    vat_number: 'VAT123456',
    name_postfix: '(Old System)',
    street_address: '123 Legacy Street',
    street_address_2: 'Suite 456',
    zip_code: '12345',
    city: 'Legacy City',
    notes: 'This wholesaler has all legacy fields populated for testing',
    make_department_specific: false,
    department_info: null,
  })

  // EMAIL wholesaler with department-specific fields for testing
  server.create('wholesaler', {
    name: 'Department-Specific Wholesaler',
    archived: false,
    wholesaler_type: 'EMAIL',
    email: 'dept@example.com',
    phone_number: '+1 555 987 6543',
    customer_number: '',
    register_number: '',
    make_department_specific: true,
    department_info: {
      customer_number: 'DEPT-999',
      register_number: 'DEPT-REG-777',
    },
  })
}
